import { useState, useEffect, useCallback } from "react";
import { VolumeX } from "lucide-react";
import { useActiveSection } from "@/hooks/useActiveSection";
import { motion, AnimatePresence } from "framer-motion";
import Magnetic from "@/components/Magnetic";
import New_Logo from "./New_Logo";

const navItems = [
  { label: "Home", href: "#home", id: "home" },
  { label: "About", href: "#about", id: "about" },
  { label: "Savings", href: "#skills", id: "skills" },
  { label: "Services", href: "#projects", id: "projects" },
  { label: "Contact", href: "#contact", id: "contact" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const active = useActiveSection();

  /* ── Navigation Handler ── */
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const element = document.getElementById(id);
    if (element) {
      if ((window as any).lenis) {
        (window as any).lenis.scrollTo(element, { offset: -100 });
      } else {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: "smooth"
        });
      }
    }
    setMobileOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-lg border-b border-border/40 py-3 shadow-md"
    >
      <nav className="max-w-6xl mx-auto px-6 flex items-center justify-between">
        <Magnetic strength={0.2}>
          <a
            href="#home"
            className="block group"
            onClick={(e) => scrollToSection(e, "#home")}
          >
            <New_Logo />
          </a>
        </Magnetic>

        {/* ── Desktop nav ── */}
        <ul className="hidden md:flex items-center gap-2">
          {navItems.map((item) => (
            <li key={item.label}>
              <Magnetic strength={0.15}>
                <a
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className={`px-4 py-2 font-body text-sm tracking-wide transition-colors duration-200 relative block rounded-full color-shift ${active === item.id ? "text-pulse" : "text-foreground-muted hover:text-pulse"
                    }`}
                >
                  {item.label}

                  {/* Pill background indicator */}
                  {active === item.id && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 bg-pulse/10 rounded-full -z-10 border border-pulse/20"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}

                  {/* Drawing underline (left‑to‑right) */}
                  <motion.span
                    className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-gradient-to-r from-pulse to-electric"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: active === item.id ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                    style={{ originX: 0 }}
                  />
                </a>
              </Magnetic>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <Magnetic strength={0.3}>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, "#contact")}
              className="inline-flex items-center px-6 py-2.5 rounded-full bg-pulse text-primary-foreground text-sm font-body font-medium tracking-wide shadow-pulse hover:opacity-90 transition-all duration-300 haptic-hover"
            >
              Get Started
            </a>
          </Magnetic>
        </div>

        {/* ── Creative hamburger animation ── */}
        <button
          className="md:hidden relative w-7 h-5 flex flex-col justify-between items-stretch p-0 bg-transparent border-none"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <motion.span
            animate={mobileOpen ? { rotate: 45, y: 9 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full h-[2px] bg-foreground block rounded-full origin-center"
          />
          <motion.span
            animate={mobileOpen ? { opacity: 0, x: -20 } : { opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full h-[2px] bg-foreground block rounded-full"
          />
          <motion.span
            animate={mobileOpen ? { rotate: -45, y: -9 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full h-[2px] bg-foreground block rounded-full origin-center"
          />
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-card border-t border-border/50 px-6 overflow-hidden"
          >
            <div className="py-8 flex flex-col gap-6">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className={`font-display text-2xl transition-all duration-300 color-shift ${active === item.id
                    ? "text-pulse"
                    : "text-foreground-muted hover:text-pulse hover:scale-105 hover:font-bold"
                    }`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, "#contact")}
                className="mt-4 px-6 py-3 bg-pulse text-primary-foreground rounded-full text-center font-body font-bold"
              >
                Get Started
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
